import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { createTicket, listMyTickets, getMyTicket, replyToTicket } from "@/lib/support.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LifeBuoy, Plus, Send, ArrowLeft, Loader2 } from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";

type TFn = ReturnType<typeof useHydratedTranslation>;

export const Route = createFileRoute("/_authenticated/help")({
  head: () =>
    buildSeo({
      path: "/help",
      title: "Help & Support — Reality Twin",
      description: "Open a support ticket and track replies from the Reality Twin team.",
      noindex: true,
    }),
  component: HelpPage,
});

const STATUS_STYLE: Record<string, string> = {
  open: "bg-[oklch(0.82_0.18_168_/_0.12)] text-[oklch(0.82_0.18_168)] border-[oklch(0.82_0.18_168_/_0.3)]",
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  resolved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  closed: "bg-white/5 text-muted-foreground border-border",
};
const STATUS_KEY: Record<string, string> = {
  open: "stOpen",
  pending: "stPending",
  resolved: "stResolved",
  closed: "stClosed",
};
function relTime(iso: string, t: TFn): string {
  const m = Math.floor((Date.now() - Date.parse(iso)) / 60000);
  if (m < 1) return t("app.pages.help.justNow");
  if (m < 60) return t("app.pages.help.minAgo", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("app.pages.help.hourAgo", { n: h });
  return t("app.pages.help.dayAgo", { n: Math.floor(h / 24) });
}

function HelpPage() {
  const t = useHydratedTranslation();
  const fetchList = useServerFn(listMyTickets);
  const create = useServerFn(createTicket);
  const fetchTicket = useServerFn(getMyTicket);
  const reply = useServerFn(replyToTicket);

  const [hasSession, setHasSession] = useState(false);
  useEffect(() => {
    let m = true;
    supabase.auth.getSession().then(({ data }) => m && setHasSession(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setHasSession(!!s));
    return () => {
      m = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const [tickets, setTickets] = useState<any[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [thread, setThread] = useState<any>(null);
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "", body: "" });
  const [replyBody, setReplyBody] = useState("");
  const [busy, setBusy] = useState(false);

  const loadList = () => fetchList().then((r) => setTickets(r.tickets));
  useEffect(() => {
    if (hasSession) loadList();
  }, [hasSession]);

  async function openTicket(id: string) {
    setOpenId(id);
    setThread(null);
    try {
      setThread(await fetchTicket({ data: { ticketId: id } }));
      loadList();
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.help.couldNotOpen"));
    }
  }

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim() || busy) return;
    setBusy(true);
    try {
      const r = await create({
        data: {
          subject: form.subject,
          category: form.category || undefined,
          body: form.body,
        },
      });
      toast.success(t("app.pages.help.ticketCreated"));
      setComposing(false);
      setForm({ subject: "", category: "", body: "" });
      await loadList();
      openTicket(r.id);
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.help.couldNotCreate"));
    } finally {
      setBusy(false);
    }
  }

  async function submitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyBody.trim() || !openId || busy) return;
    setBusy(true);
    try {
      await reply({ data: { ticketId: openId, body: replyBody } });
      setReplyBody("");
      await openTicket(openId);
    } catch (e: any) {
      toast.error(e?.message ?? t("app.pages.help.couldNotSend"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <DashboardShell
      title={t("app.pages.help.title")}
      subtitle={t("app.pages.help.subtitle")}
      actions={
        !composing && !openId ? (
          <button
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-1.5 rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium"
          >
            <Plus className="h-4 w-4" /> {t("app.pages.help.newTicket")}
          </button>
        ) : undefined
      }
    >
      {/* New ticket form */}
      {composing && (
        <form onSubmit={submitNew} className="glass-strong rounded-2xl p-6 max-w-2xl space-y-4">
          <div className="font-display text-lg font-semibold">
            {t("app.pages.help.newTicketForm")}
          </div>
          <input
            required
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder={t("app.pages.help.subjectPh")}
            maxLength={160}
            className={inp}
          />
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className={inp}
          >
            <option value="">{t("app.pages.help.catGeneral")}</option>
            <option value="billing">{t("app.pages.help.catBilling")}</option>
            <option value="technical">{t("app.pages.help.catTechnical")}</option>
            <option value="feature">{t("app.pages.help.catFeature")}</option>
            <option value="account">{t("app.pages.help.catAccount")}</option>
          </select>
          <textarea
            required
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder={t("app.pages.help.bodyPh")}
            rows={6}
            maxLength={4000}
            className={`${inp} resize-none`}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-full brand-gradient text-black px-5 py-2 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              {t("app.pages.help.sendTicket")}
            </button>
            <button
              type="button"
              onClick={() => setComposing(false)}
              className="rounded-full border border-border px-5 py-2 text-sm hover:bg-white/5"
            >
              {t("app.pages.common.cancel")}
            </button>
          </div>
        </form>
      )}

      {/* Open ticket thread */}
      {openId && thread && !composing && (
        <div className="max-w-2xl">
          <button
            onClick={() => {
              setOpenId(null);
              setThread(null);
            }}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
          >
            <ArrowLeft className="h-3 w-3" /> {t("app.pages.help.allTickets")}
          </button>
          <div className="glass-strong rounded-2xl p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="font-display text-lg font-semibold">{thread.ticket.subject}</div>
              <StatusBadge status={thread.ticket.status} />
            </div>
            <div className="mt-5 space-y-4">
              {thread.messages.map((m: any) => (
                <div
                  key={m.id}
                  className={`flex ${m.author_role === "customer" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.author_role === "customer"
                        ? "bg-foreground text-background rounded-br-sm"
                        : "glass border border-border rounded-bl-sm"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wide opacity-60 mb-1">
                      {m.author_role === "customer"
                        ? t("app.pages.help.you")
                        : t("app.pages.help.supportTeam")}{" "}
                      · {relTime(m.created_at, t)}
                    </div>
                    {m.body}
                  </div>
                </div>
              ))}
            </div>
            {thread.ticket.status !== "closed" && (
              <form onSubmit={submitReply} className="mt-5 flex gap-2 border-t border-border pt-4">
                <input
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder={t("app.pages.help.replyPh")}
                  className={`${inp} flex-1`}
                />
                <button
                  type="submit"
                  disabled={busy || !replyBody.trim()}
                  className="rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium disabled:opacity-50 shrink-0"
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Ticket list */}
      {!openId && !composing && (
        <div className="max-w-2xl">
          {tickets === null ? (
            <div className="space-y-2">
              {[0, 1].map((n) => (
                <div key={n} className="h-16 rounded-xl glass-strong animate-pulse" />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="glass-strong rounded-2xl p-10 text-center">
              <LifeBuoy className="h-8 w-8 mx-auto text-muted-foreground" />
              <div className="mt-3 font-medium">{t("app.pages.help.noTickets")}</div>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("app.pages.help.noTicketsSub")}
              </p>
              <button
                onClick={() => setComposing(true)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium"
              >
                <Plus className="h-4 w-4" /> {t("app.pages.help.newTicket")}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => openTicket(ticket.id)}
                  className="w-full text-left glass-strong rounded-xl p-4 hover:bg-white/5 transition flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {ticket.unread_for_customer && (
                        <span className="h-2 w-2 rounded-full bg-[oklch(0.82_0.18_168)] shrink-0" />
                      )}
                      {ticket.subject}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {ticket.category
                        ? t(
                            `app.pages.help.cat${ticket.category[0].toUpperCase() + ticket.category.slice(1)}`,
                            { defaultValue: ticket.category },
                          ) + " · "
                        : ""}
                      {t("app.pages.help.updated", { time: relTime(ticket.last_message_at, t) })}
                    </div>
                  </div>
                  <StatusBadge status={ticket.status} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </DashboardShell>
  );
}

function StatusBadge({ status }: { status: string }) {
  const t = useHydratedTranslation();
  return (
    <span
      className={`shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLE[status] ?? STATUS_STYLE.closed}`}
    >
      {t(`app.pages.help.${STATUS_KEY[status] ?? "stClosed"}`, { defaultValue: status })}
    </span>
  );
}

const inp =
  "w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]";
